import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import styled from 'styled-components'
import { BUTTONS, ERRORS } from 'locales/locales'
import { FORM } from 'locales/locales'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'app/hooks'
import { Form, Field, FormElement, FormRenderProps } from '@progress/kendo-react-form'
import { Button } from '@progress/kendo-react-buttons'
import { FormInput, FormNumericTextBox } from 'components/kendo/form/FormComponents'
import Uploader, { UploaderRef } from 'components/kendo/upload/Upload'
import { FixMeLater } from 'types'
import { useLazyGetSystemQuery, useUpdateSystemSettingsMutation } from 'app/services/system.service'
import Loading from 'components/spinner/Loading'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import CustomButton from 'components/button/CustomButton'

const AdminSystemSettings: React.FC = () => {
  const { t } = useTranslation()
  const systemSettingsSlice = useAppSelector((state) => state.system.data)

  const [updateSystem] = useUpdateSystemSettingsMutation()
  const [getSystemSettings] = useLazyGetSystemQuery()

  // Upload componentleri için ref'ler
  const logoUploaderRef = useRef<UploaderRef>(null)
  const faviconUploaderRef = useRef<UploaderRef>(null)

  const [systemSettings, setSystemSettings] = useState({
    name: systemSettingsSlice?.name || '',
    panelName: systemSettingsSlice?.panelName || '',
    inputMaxChar: systemSettingsSlice?.inputMaxChar || '',
  })

  const onChangeInput = (value: string, key: string) => {
    const newValue = key === 'inputMaxChar' ? parseInt(value, 10) : value
    setSystemSettings((prevSettings) => ({
      ...prevSettings,
      [key]: newValue,
    }))
  }

  useEffect(() => {
    fetchSystemSettings()
  }, [])

  const fetchSystemSettings = useCallback(async () => {
    try {
      Loading.ref.show()
      const response = await getSystemSettings({})
      if (response?.data?.data && Object.keys(response.data.data).length !== 0) {
        const { name = '', panelName = '', inputMaxChar = '' } = response.data.data
        setSystemSettings({
          name: name,
          panelName: panelName,
          inputMaxChar: inputMaxChar,
        })
      } else {
        setSystemSettings(systemSettingsSlice)
      }
    } catch (error) {
      console.error('Error fetching system settings:', error)
    } finally {
      Loading.ref.hide()
    }
  }, [getSystemSettings, systemSettingsSlice])

  const handleSubmit = useCallback(async () => {
    try {
      if (logoUploaderRef.current?.hasSelectedFile()) {
        await logoUploaderRef.current.performUpload()
      }

      if (faviconUploaderRef.current?.hasSelectedFile()) {
        await faviconUploaderRef.current.performUpload()
      }
    
      const system: FixMeLater = await updateSystem({
        ...systemSettings,
        // imageUrlFavicon: '/files/system/favicon.png',
        // imageUrlLogo: '/files/system/logo.png',
        _id: systemSettingsSlice?._id,
      })
      if (system.data.success) {
        fetchSystemSettings()
      }
    } catch (error) {
      DropdownAlert.ref.show({
        isError: true,
        message: t(ERRORS.SOMETHING_WENT_WRONG_ERROR),
      })
    }
  }, [updateSystem, systemSettings, systemSettingsSlice._id, fetchSystemSettings, t])

/*   const isFormChanged = useMemo(() => {
    return (
      systemSettings.name !== systemSettingsSlice?.name ||
      systemSettings.panelName !== systemSettingsSlice?.panelName ||
      systemSettings.inputMaxChar !== systemSettingsSlice?.inputMaxChar
    )
  }, [
    systemSettings.name,
    systemSettingsSlice?.name,
    systemSettings.panelName,
    systemSettingsSlice?.panelName,
    systemSettings.inputMaxChar,
    systemSettingsSlice?.inputMaxChar,
  ])
 */
  return (
    <ContentCard>
      <ContentCardHeader>{t(FORM.SYSTEM_SETTINGS_TITLE)}</ContentCardHeader>
      <ContentCardBody>
        <Form
          onSubmit={handleSubmit}
          initialValues={{
            name: systemSettings.name,
            panelName: systemSettings.panelName,
            inputMaxChar: systemSettings.inputMaxChar?.toString(),
          }}
          render={() => (
            <FormElement>
              <MainLayout>
                <LeftColumn>
                  <InputsSection>
                    <SettingsRow>
                      <SettingsLabel>{t(FORM.LOGO)}</SettingsLabel>
                      <SettingsContent>
                        <Uploader
                          ref={logoUploaderRef}
                          fileUrl={systemSettingsSlice?.imageUrlLogo}
                          type="logo"
                          manualUpload={true}
                          restrictions={{
                            allowedExtensions: [".png"],
                            maxFileSize: 1024 * 1024 * 50,
                          }}
                          accept="image/png"
                        />
                      </SettingsContent>
                    </SettingsRow>

                    <SettingsRow>
                      <SettingsLabel>{t(FORM.FAVICON)}</SettingsLabel>
                      <SettingsContent>
                        <Uploader
                          ref={faviconUploaderRef}
                          fileUrl={systemSettingsSlice?.imageUrlFavicon}
                          type="favicon"
                          manualUpload={true}
                          restrictions={{
                            allowedExtensions: [".png"],
                            maxFileSize: 1024 * 1024 * 50,
                          }}
                          accept="image/png"
                        />
                      </SettingsContent>
                    </SettingsRow>

                    <SettingsRow>
                      <SettingsLabel style={{ marginTop: '10px' }}>
                        {t(FORM.SYSTEM_NAME)}
                      </SettingsLabel>
                      <SettingsContent style={{ width: '350px' }}>
                        <SettingsInputField
                          id="name"
                          name="name"
                          component={FormInput}
                          onChange={(e: FixMeLater) => onChangeInput(e.value, 'name')}
                        />
                      </SettingsContent>
                    </SettingsRow>

                    <SettingsRow>
                      <SettingsLabel style={{ marginTop: '10px' }}>
                        {t(FORM.MANAGEMENT_PANEL_NAME)}
                      </SettingsLabel>
                      <SettingsContent style={{ width: '350px' }}>
                        <SettingsInputField
                          id="panelName"
                          name="panelName"
                          component={FormInput}
                          onChange={(e: FixMeLater) => onChangeInput(e.value, 'panelName')}
                        />
                      </SettingsContent>
                    </SettingsRow>

                    <SettingsRow>
                      <SettingsLabel style={{ marginTop: '10px' }}>
                        {t(FORM.MAX_CHAR_INPUT)}
                      </SettingsLabel>
                      <SettingsContent style={{ width: '350px' }}>
                        <SettingsInputField
                          id="inputMaxChar"
                          name="inputMaxChar"
                          value={systemSettings.inputMaxChar?.toString()}
                          component={FormNumericTextBox}
                          hint={t(FORM.MAX_CHAR_INPUT_PLACEHOLDER)}
                          onChange={(e: FixMeLater) => onChangeInput(e.value, 'inputMaxChar')}
                        />
                      </SettingsContent>
                    </SettingsRow>
                    <SettingsRow>
                      <SettingsLabel></SettingsLabel>
                      <ButtonContent style={{ width: '350px' }}>
                        <CustomStyledButton
                          text={t(BUTTONS.SAVE)}
                          onClick={handleSubmit}
                        />
                      </ButtonContent>
                    </SettingsRow>
                  </InputsSection>
                </LeftColumn>
              </MainLayout>
            </FormElement>
          )}
        />
      </ContentCardBody>
    </ContentCard>
  )
}

export default AdminSystemSettings

const ContentCard = styled.div`
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2), 0 0 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid #e3ebf6;
`

const ContentCardHeader = styled.div`
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 500;
  border-bottom: 1px solid #e3ebf6;
`

const ContentCardBody = styled.div`
  padding: 20px;
`

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
`

const InputsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SettingsRow = styled.div`
  display: grid;
  grid-template-columns: 177px 1fr;
  gap: 50px;
  align-items: start;
  min-height: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`

const SettingsLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  padding-top: 8px;
`

const SettingsContent = styled.div`
  display: flex;
  align-items: center;
`

const SettingsInputField = styled(Field)`
  max-width: 100%;
`

const ButtonContent = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
`

const CustomStyledButton = styled(CustomButton)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background-color: #1f4ab7;
  margin-left: auto;
`
